import PageHeader from "../components/PageHeader";
import { stats, impactByMonth, categoryDistribution } from "../lib/mockData";
import { Card } from "../components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend, AreaChart, Area,
} from "recharts";
import { Users, Globe, Utensils, Briefcase, Sparkles, Heart } from "lucide-react";

const COLORS = ["hsl(203, 69%, 36%)", "hsl(148, 34%, 57%)", "hsl(25, 85%, 65%)", "hsl(260, 45%, 60%)", "hsl(200, 90%, 55%)"];

export default function Stats() {
  const kpis = [
    { icon: Users, label: "Neighbors helped", value: stats.neighborsHelped, color: "bg-primary/10 text-primary" },
    { icon: Heart, label: "Active volunteers", value: stats.activeVolunteers, color: "bg-secondary/15 text-secondary" },
    { icon: Globe, label: "Countries", value: stats.countries, color: "bg-primary/10 text-primary" },
    { icon: Utensils, label: "Meals delivered", value: stats.mealsDelivered, color: "bg-secondary/15 text-secondary" },
    { icon: Sparkles, label: "Matches made", value: stats.matchesMade, color: "bg-primary/10 text-primary" },
    { icon: Briefcase, label: "Jobs filled", value: stats.jobsFilled, color: "bg-secondary/15 text-secondary" },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Impact Dashboard"
        title="Every number is a person, a meal, a hand held."
        description="Live statistics of what our community achieves together — updated in real time."
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        {/* KPI grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
          {kpis.map((k, i) => (
            <Card
              key={k.label}
              data-testid={`stat-kpi-${i}`}
              className={`fade-up stagger-${(i % 6) + 1} rounded-2xl p-5 border-border/60 shadow-none`}
            >
              <div className={`h-9 w-9 rounded-xl ${k.color} flex items-center justify-center mb-3`}>
                <k.icon size={16} />
              </div>
              <div className="text-2xl font-display font-bold">{k.value.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground mt-1">{k.label}</div>
            </Card>
          ))}
        </div>

        {/* Charts */}
        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          <Card className="lg:col-span-2 rounded-3xl p-6 sm:p-8 border-border/60 shadow-none">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-display text-xl font-semibold">Helps vs. Needs</h3>
                <p className="text-sm text-muted-foreground mt-1">Monthly community activity</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={impactByMonth}>
                <defs>
                  <linearGradient id="c1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(203, 69%, 36%)" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(203, 69%, 36%)" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="c2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(148, 34%, 57%)" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(148, 34%, 57%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(40, 15%, 88%)" />
                <XAxis dataKey="month" stroke="hsl(205, 15%, 40%)" fontSize={12} />
                <YAxis stroke="hsl(205, 15%, 40%)" fontSize={12} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(40, 15%, 88%)" }} />
                <Area type="monotone" dataKey="helps" stroke="hsl(203, 69%, 36%)" fill="url(#c1)" strokeWidth={2} />
                <Area type="monotone" dataKey="needs" stroke="hsl(148, 34%, 57%)" fill="url(#c2)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>

          <Card className="rounded-3xl p-6 sm:p-8 border-border/60 shadow-none">
            <h3 className="font-display text-xl font-semibold mb-1">Category mix</h3>
            <p className="text-sm text-muted-foreground mb-5">Where help flows</p>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={categoryDistribution}
                  innerRadius={60}
                  outerRadius={95}
                  dataKey="value"
                  paddingAngle={3}
                >
                  {categoryDistribution.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(40, 15%, 88%)" }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </div>

        <Card className="rounded-3xl p-6 sm:p-8 border-border/60 shadow-none">
          <h3 className="font-display text-xl font-semibold mb-1">Monthly matches trend</h3>
          <p className="text-sm text-muted-foreground mb-5">Total AI-driven pairings per month</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={impactByMonth}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(40, 15%, 88%)" />
              <XAxis dataKey="month" stroke="hsl(205, 15%, 40%)" fontSize={12} />
              <YAxis stroke="hsl(205, 15%, 40%)" fontSize={12} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(40, 15%, 88%)" }} />
              <Bar dataKey="helps" fill="hsl(203, 69%, 36%)" radius={[10, 10, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </>
  );
}
